/**
 * Form for pausing a patient with duration and reason.
 * @agentic connections
 */

// Components
import {
  Stack,
  NumberInput,
  Textarea,
  Button,
  Group,
  Text,
  Alert,
} from "@mantine/core";
import { IconAlertCircle, IconPlayerPause } from "@tabler/icons-react";

// Hooks
import { useState } from "react";
import { useMedplum } from "@medplum/react";
import { notifications } from "@mantine/notifications";

// Types
import type { Patient } from "@medplum/fhirtypes";

// Workflows
import { pausePatient } from "@benchmark/core/workflows";

// Interfaces
/**
 * Props for the pause patient form.
 * @agentic connections
 */
export type PausePatientFormProps = {
  patient: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/**
 * Form for pausing a patient with duration and reason.
 * @agentic connections
 */
export const PausePatientForm = ({
  patient,
  onSuccess,
  onCancel,
}: PausePatientFormProps) => {
  const medplum = useMedplum();
  const [duration, setDuration] = useState<number>(14); // Default 14 days
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError("Pause reason is required");
      return;
    }

    if (!duration || duration <= 0) {
      setError("Duration must be greater than 0");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!patient.id) {
        throw new Error("Patient ID is required");
      }
      await pausePatient(medplum, {
        patientId: patient.id,
        reason: reason.trim(),
        durationDays: duration,
      });

      notifications.show({
        title: "Patient Paused",
        message: `${patient.name?.[0]?.given?.[0] || "Patient"} has been paused for ${duration} days`,
        color: "blue",
        icon: <IconPlayerPause size={16} />,
      });

      onSuccess?.();
    } catch (err) {
      console.error("Error pausing patient:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to pause patient. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Pausing a patient will temporarily halt their care plan activities and
        adjust task due dates. Open tasks will be rescheduled to resume after
        the pause period.
      </Text>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          variant="light"
        >
          {error}
        </Alert>
      )}

      <NumberInput
        label="Pause Duration (days)"
        description="Number of days to pause the patient"
        placeholder="14"
        value={duration}
        onChange={(val) => setDuration(Number(val))}
        min={1}
        max={365}
        required
      />

      <Textarea
        label="Reason for Pause"
        description="Explain why the patient is being paused"
        placeholder="e.g., Patient requested temporary hold, family emergency, etc."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        minRows={3}
        required
      />

      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          leftSection={<IconPlayerPause size={16} />}
        >
          Pause Patient
        </Button>
      </Group>
    </Stack>
  );
};
